import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { works } from '@/mocks/works';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const featuredWorks = useMemo(() => works.filter((w) => w.featured), []);
  const total = featuredWorks.length;

  const go = useCallback(
    (next: number) => {
      setIndex((next + total) % total);
    },
    [total],
  );

  useEffect(() => {
    if (paused || total <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 5200);
    return () => clearInterval(timer);
  }, [paused, total]);

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 48) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  if (total === 0) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
        <div className="rounded-lg border border-background-200 bg-background-100 py-20 text-center text-foreground-500">
          暂无相关内容，敬请期待
        </div>
      </div>
    );
  }

  const current = featuredWorks[index];
  const typeLabel = current.type === 'movie' ? '电影' : '电视剧';

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative h-[440px] w-full md:h-[560px]">
        {featuredWorks.map((w, i) => (
          <img
            key={w.id}
            src={w.backdrop}
            alt={w.title}
            title={`${w.title} 焦点推荐`}
            className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-[900ms] ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/70 to-primary-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent" />
      </div>

      <div className="absolute inset-0 flex items-center">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div key={current.id} className="animate-fade-up max-w-2xl">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                焦点推荐
              </span>
              <span className="rounded-full border border-primary-300/50 px-3 py-1 text-xs text-background-50">
                {typeLabel}
              </span>
              <span className="text-xs text-primary-100">{current.year} · {current.region}</span>
            </div>
            <h1 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              {current.title}
            </h1>
            <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-primary-100 md:text-base">
              {current.summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-circle-line text-lg" />
                查看详情
              </Link>
              <Link
                to={`/category/${current.type}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300/60 bg-primary-900/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-800"
              >
                进入{typeLabel}分类
              </Link>
              <span className="inline-flex items-center gap-1 text-accent-300">
                <i className="ri-star-fill" />
                <span className="text-sm font-semibold">{current.rating.toFixed(1)}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-primary-800 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-primary-800 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {featuredWorks.map((w, i) => (
          <button
            key={w.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === index ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
}